{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2020 - 2026                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCOpenLayers;

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF WEBLIB}
{$DEFINE LCLWEBLIB}
{$ENDIF}
{$IFDEF LCLLIB}
{$DEFINE LCLWEBLIB}
{$ENDIF}

interface

uses
  Classes, Types, WEBLib.Forms
  {$IFNDEF LCLWEBLIB}
  ,Generics.Collections
  {$ENDIF}
  {$IFDEF LCLLIB}
  ,fgl
  {$ENDIF}
  {$IFDEF WEBLIB}
  ,web, Contnrs
  {$ENDIF}
  ,WEBLib.TMSFNCWebBrowser
  ,WEBLib.TMSFNCMapsCommonTypes
  ,WEBLib.TMSFNCTypes
  ,WEBLib.TMSFNCGraphicsTypes
  ,WEBLib.TMSFNCMaps;

const
  MAJ_VER = 4; // Major version nr.
  MIN_VER = 2; // Minor version nr.
  REL_VER = 2; // Release nr.
  BLD_VER = 0; // Build nr.

  //v1.0.0.0: First release
  //        : New : Options.TileServer property
  //v1.0.1.0: New : Support for TileLayers
  //v1.0.2.0: New : Support to update TileLayers
  //v1.1.0.0: New : Support for Polyline/Polygon Labels
  //        : New : Improved TileServer updating
  //        : Fixed : Issue with flickering when updating maps
  //v1.1.1.0: New : Added Polyline.Label.BackgroundColor/BorderColor/Padding properties
  //v1.1.1.1: Improved : Polyline.Label.BackgroundColor/BorderColor rendering
  //v1.2.0.0: New : Draggable Markers and Poly elements
  //v1.2.0.1: Fixed : Issue with popup visible when dragging markers
  //v1.3.0.0: New : OnMapRenderComplete event added
  //v1.3.0.1: Fixed : Issue with popup position after marker changed position
  //v1.3.0.2: Fixed : API URLs updated
  //v1.4.0.0: New : Complex Polygons support added
  //v1.5.0.0: New : Marker Clusters support added
  //v1.6.0.0: New : Support for WMS and GeoJSON type tilelayers
  //        : New : LibraryLocation property for offline support
  //        : New : Options.ShowBaseLayer property
  //v1.6.0.1: Fixed : Issue with MarkerMouseEnter/MarkerMouseLeave events
  //v2.0.0.0: New : OpenLayers v8 API compatibilty
  //v3.0.0.0: New : OpenLayers v9 API compatibilty
  //        : Fixed : Issue with CloseAllPopups in TMS WEB Core
  //v3.0.0.1: Fixed : Issue with Runtime Packages
  //v4.0.0.0: New : OpenLayers v10 API compatibilty
  //        : New : HeatMaps added
  //        : New : Options.Version property added to select the OpenLayers API version
  //        : New : OnMapRightClick, OnMarkerRightClick, OnPolyElementRightClick events
  //v4.1.0.0: New : Layers CSSClassName property added
  //        : New : Clusters: ImagePath, FillColor, FontColor, StrokeColor added
  //v4.2.0.0: New : Default API version updated to v10.7.0
  //        : New : Support for GeoTiff type tilelayers
  //v4.2.1.0: New : Marker Anchor, DefaultAnchor
  //v4.2.2.0: Fixed : OpenStreetMap compatibility

type
  TTMSFNCCustomOpenLayers = class;
  TTMSFNCOpenLayersCluster = class;
  TTMSFNCOpenLayersLabelClass = class of TTMSFNCOpenLayersLabel;

  TTMSFNCOpenLayersLabel = class(TPersistent)
  private
    FOnChange: TNotifyEvent;
    FText: string;
    FOffsetX: Double;
    FOffsetY: Double;
    FFontSize: Integer;
    FFontColor: TTMSFNCGraphicsColor;
    FBorderColor: TTMSFNCGraphicsColor;
    FPadding: Integer;
    FBackgroundColor: TTMSFNCGraphicsColor;
    procedure SetBackgroundColor(const Value: TTMSFNCGraphicsColor);
    procedure SetBorderColor(const Value: TTMSFNCGraphicsColor);
    procedure SetFontColor(const Value: TTMSFNCGraphicsColor);
    procedure SetFontSize(const Value: Integer);
    procedure SetOffsetX(const Value: Double);
    procedure SetOffsetY(const Value: Double);
    procedure SetPadding(const Value: Integer);
    procedure SetText(const Value: string);
  protected
    procedure Changed;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create; virtual;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  published
    property Text: string read FText write SetText;
    property OffsetX: Double read FOffsetX write SetOffsetX;
    property OffsetY: Double read FOffsetY write SetOffsetY;
    property FontColor: TTMSFNCGraphicsColor read FFontColor write SetFontColor default gcBlack;
    property FontSize: Integer read FFontSize write SetFontSize default 12;
    property BackgroundColor: TTMSFNCGraphicsColor read FBackgroundColor write SetBackgroundColor default gcWhite;
    property BorderColor: TTMSFNCGraphicsColor read FBorderColor write SetBorderColor default gcBlack;
    property Padding: Integer read FPadding write SetPadding default 3;
  end;

  TTMSFNCOpenLayersPolygon = class(TTMSFNCMapsPolygon)
  private
    FLabel: TTMSFNCOpenLayersLabel;
    FDraggable: Boolean;
    procedure SetLabel(const Value: TTMSFNCOpenLayersLabel);
    procedure SetDraggable(const Value: Boolean);
  public
    constructor Create(ACollection: TCollection); override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
    procedure LabelChanged(Sender: TObject);
    function AddHole(ACoordinates: TTMSFNCMapsCoordinateRecArray): TTMSFNCMapsPolyElementHole; override;
  published
    property Draggable: Boolean read FDraggable write SetDraggable default false;
    property &Label: TTMSFNCOpenLayersLabel read FLabel write SetLabel;
    property Holes;
  end;

  TTMSFNCOpenLayersPolygons = class(TTMSFNCMapsPolygons)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersPolygon;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersPolygon);
  protected
    function CreateItemClass: TCollectionItemClass; override;
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersPolygon read GetItem write SetItem; default;
    function Add: TTMSFNCOpenLayersPolygon;
    function Insert(Index: Integer): TTMSFNCOpenLayersPolygon;
  end;

  TTMSFNCOpenLayersPolyline = class(TTMSFNCMapsPolyline)
  private
    FLabel: TTMSFNCOpenLayersLabel;
    FDraggable: Boolean;
    procedure SetLabel(const Value: TTMSFNCOpenLayersLabel);
    procedure SetDraggable(const Value: Boolean);
  public
    constructor Create(ACollection: TCollection); override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
    procedure LabelChanged(Sender: TObject);
  published
    property &Label: TTMSFNCOpenLayersLabel read FLabel write SetLabel;
    property Draggable: Boolean read FDraggable write SetDraggable default False;
  end;

  TTMSFNCOpenLayersPolylines = class(TTMSFNCMapsPolylines)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersPolyline;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersPolyline);
  protected
    function CreateItemClass: TCollectionItemClass; override;
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersPolyline read GetItem write SetItem; default;
    function Add: TTMSFNCOpenLayersPolyline;
    function Insert(Index: Integer): TTMSFNCOpenLayersPolyline;
  end;

  TTMSFNCOpenLayersCircle = class(TTMSFNCMapsCircle)
  private
    FDraggable: Boolean;
    procedure SetDraggable(const Value: Boolean);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
  published
    property Draggable: Boolean read FDraggable write SetDraggable default False;
  end;

  TTMSFNCOpenLayersCircles = class(TTMSFNCMapsCircles)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersCircle;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersCircle);
  protected
    function CreateItemClass: TCollectionItemClass; override;
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersCircle read GetItem write SetItem; default;
    function Add: TTMSFNCOpenLayersCircle;
    function Insert(Index: Integer): TTMSFNCOpenLayersCircle;
  end;

  TTMSFNCOpenLayersRectangle = class(TTMSFNCMapsRectangle)
  private
    FDraggable: Boolean;
    procedure SetDraggable(const Value: Boolean);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
  published
    property Draggable: Boolean read FDraggable write SetDraggable default False;
  end;

  TTMSFNCOpenLayersRectangles = class(TTMSFNCMapsRectangles)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersRectangle;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersRectangle);
  protected
    function CreateItemClass: TCollectionItemClass; override;
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersRectangle read GetItem write SetItem; default;
    function Add: TTMSFNCOpenLayersRectangle;
    function Insert(Index: Integer): TTMSFNCOpenLayersRectangle;
  end;

  TTMSFNCOpenLayersMarker = class(TTMSFNCMapsMarker)
  private
    FDraggable: Boolean;
    FCluster: TTMSFNCOpenLayersCluster;
    FAnchor: TTMSFNCMapsAnchorPoint;
    FDefaultAnchor: Boolean;
    procedure SetDraggable(const Value: Boolean);
    procedure SetAnchor(const Value: TTMSFNCMapsAnchorPoint);
    procedure SetDefaultAnchor(const Value: Boolean);
  protected
    procedure DoAnchorChanged(Sender: TObject);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
    property Cluster: TTMSFNCOpenLayersCluster read FCluster write FCluster;
  published
    property Draggable: Boolean read FDraggable write SetDraggable default False;
    property Anchor: TTMSFNCMapsAnchorPoint read FAnchor write SetAnchor;
    property DefaultAnchor: Boolean read FDefaultAnchor write SetDefaultAnchor default True;
  end;

  TTMSFNCOpenLayersMarkers = class(TTMSFNCMapsMarkers)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersMarker;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersMarker);
  protected
    function CreateItemClass: TCollectionItemClass; override;
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersMarker read GetItem write SetItem; default;
    function Add: TTMSFNCOpenLayersMarker;
    function Insert(Index: Integer): TTMSFNCOpenLayersMarker;
  end;

  {$IFDEF WEBLIB}
  TTMSFNCOpenLayersMarkersList = class(TList)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersMarker;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersMarker);
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersMarker read GetItem write SetItem; default;
  end;
  {$ENDIF}
  {$IFNDEF WEBLIB}
  TTMSFNCOpenLayersMarkersList = class(TList<TTMSFNCOpenLayersMarker>);
  {$ENDIF}

  TTMSFNCOpenLayersCluster = class(TCollectionItem)
  private
    FReload: Boolean;
    FMarkers: TTMSFNCOpenLayersMarkersList;
    FID: string;
    FOwner: TTMSFNCCustomMaps;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FDataPointer: Pointer;
    FRecreate: Boolean;
    FImagePath: string;
    FFontColor: TTMSFNCGraphicsColor;
    FFillColor: TTMSFNCGraphicsColor;
    FStrokeColor: TTMSFNCGraphicsColor;
    function GetID: string;
    function GetMarkers: TTMSFNCOpenLayersMarkersList;
    procedure SetFontColor(const Value: TTMSFNCGraphicsColor);
    procedure SetImagePath(const Value: string);
    function IsImagePathStored: Boolean;
    procedure SetFillColor(const Value: TTMSFNCGraphicsColor);
    procedure SetStrokeColor(const Value: TTMSFNCGraphicsColor);
  protected
    procedure UpdateCluster; virtual;
  public
    constructor Create(ACollection: TCollection); override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
    property Markers: TTMSFNCOpenLayersMarkersList read GetMarkers;
    property Recreate: Boolean read FRecreate write FRecreate;
  published
    property ID: string read GetID;
    property ImagePath: string read FImagePath write SetImagePath stored IsImagePathStored nodefault;
    property StrokeColor: TTMSFNCGraphicsColor read FStrokeColor write SetStrokeColor default gcWhite;
    property FillColor: TTMSFNCGraphicsColor read FFillColor write SetFillColor default gcLightskyblue;
    property FontColor: TTMSFNCGraphicsColor read FFontColor write SetFontColor default gcWhite;
  end;

  {$IFDEF WEBLIB}
  TTMSFNCOpenLayersClusters = class(TTMSFNCOwnedCollection)
  {$ELSE}
  TTMSFNCOpenLayersClusters = class({$IFDEF LCLLIB}specialize {$ENDIF}TTMSFNCOwnedCollection<TTMSFNCOpenLayersCluster>)
  {$ENDIF}
  private
    FOwner: TTMSFNCCustomMaps;
    function GetItem(Index: Integer): TTMSFNCOpenLayersCluster;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersCluster);
  protected
    function CreateItemClass: TCollectionItemClass; virtual;
    function GetOwner: TPersistent; override;
  public
    constructor Create(AOwner: TTMSFNCCustomMaps); virtual;
    property Items[Index: Integer]: TTMSFNCOpenLayersCluster read GetItem write SetItem; default;
    procedure Clear; virtual;
    procedure Recreate; virtual;
    function Add: TTMSFNCOpenLayersCluster;
    function Insert(Index: Integer): TTMSFNCOpenLayersCluster;
  end;

  TTMSFNCOpenLayersHeatMap = class(TTMSFNCMapsHeatMap)
  public
    constructor Create(ACollection: TCollection); override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  end;

  TTMSFNCOpenLayersHeatMaps = class(TTMSFNCMapsHeatMaps)
  private
    function GetItem(Index: Integer): TTMSFNCOpenLayersHeatMap;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersHeatMap);
  protected
    function CreateItemClass: TCollectionItemClass; override;
  public
    property Items[Index: Integer]: TTMSFNCOpenLayersHeatMap read GetItem write SetItem; default;
    function Add: TTMSFNCOpenLayersHeatMap;
    function Insert(Index: Integer): TTMSFNCOpenLayersHeatMap;
  end;

  TTMSFNCOpenLayersTileLayerSource = (tlsXYZ, tlsWMS, tlsJSON, tlsGeoTiff);

  TTMSFNCOpenLayersTileLayerParams = class(TPersistent)
  private
    FOnChange: TNotifyEvent;
    FLayers: string;
    procedure SetLayers(const Value: string);
  protected
    procedure Changed;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create; virtual;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  published
    property Layers: string read FLayers write SetLayers;
  end;

  TTMSFNCOpenLayersTileLayer = class(TCollectionItem)
  private
    FReload: Boolean;
    FID: string;
    FOwner: TTMSFNCCustomMaps;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FDataPointer: Pointer;
    FRecreate: Boolean;
    FURL: string;
    FOpacity: Single;
    FSource: TTMSFNCOpenLayersTileLayerSource;
    FParams: TTMSFNCOpenLayersTileLayerParams;
    FCSSClassName: string;
    function GetID: string;
    function IsURLStored: Boolean;
    function IsOpacityStored: Boolean;
    procedure SetOpacity(const Value: Single);
    procedure SetURL(const Value: string);
    procedure SetSource(const Value: TTMSFNCOpenLayersTileLayerSource);
    procedure SetParams(const Value: TTMSFNCOpenLayersTileLayerParams);
    procedure SetCSSClassName(const Value: string);
  protected
    procedure DoParamsChanged(Sender: TObject);
    procedure UpdateTileLayer; virtual;
  public
    constructor Create(ACollection: TCollection); override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
    property Recreate: Boolean read FRecreate write FRecreate;
  published
    property ID: string read GetID;
    property URL: string read FURL write SetURL stored IsURLStored nodefault;
    property Opacity: Single read FOpacity write SetOpacity stored IsOpacityStored nodefault;
    property Source: TTMSFNCOpenLayersTileLayerSource read FSource write SetSource default tlsXYZ;
    property Params: TTMSFNCOpenLayersTileLayerParams read FParams write SetParams;
    property CSSClassName: string read FCSSClassName write SetCSSClassName;
  end;

  {$IFDEF WEBLIB}
  TTMSFNCOpenLayersTileLayers = class(TTMSFNCOwnedCollection)
  {$ELSE}
  TTMSFNCOpenLayersTileLayers = class({$IFDEF LCLLIB}specialize {$ENDIF}TTMSFNCOwnedCollection<TTMSFNCOpenLayersTileLayer>)
  {$ENDIF}
  private
    FOwner: TTMSFNCCustomMaps;
    function GetItem(Index: Integer): TTMSFNCOpenLayersTileLayer;
    procedure SetItem(Index: Integer; const Value: TTMSFNCOpenLayersTileLayer);
  protected
    function CreateItemClass: TCollectionItemClass; virtual;
    function GetOwner: TPersistent; override;
  public
    constructor Create(AOwner: TTMSFNCCustomMaps); virtual;
    property Items[Index: Integer]: TTMSFNCOpenLayersTileLayer read GetItem write SetItem; default;
    procedure Clear; virtual;
    procedure Recreate; virtual;
    function Add: TTMSFNCOpenLayersTileLayer;
    function Insert(Index: Integer): TTMSFNCOpenLayersTileLayer;
  end;

  TTMSFNCOpenLayersOptions = class(TTMSFNCMapsOptions)
  private
    FTileServer: string;
    FShowBaseLayer: Boolean;
    FVersion: string;
    procedure SetTileServer(const Value: string);
    procedure SetShowBaseLayer(const Value: Boolean);
    procedure SetVersion(const Value: string);
  public
    constructor Create; override;
  published
    property TileServer: string read FTileServer write SetTileServer;
    property ShowBaseLayer: Boolean read FShowBaseLayer write SetShowBaseLayer default True;
    property Version: string read FVersion write SetVersion;
  end;

  TTMSFNCCustomOpenLayers = class(TTMSFNCCustomMaps)
  private
    FTileLayers: TTMSFNCOpenLayersTileLayers;
    FOnPolyElementDragEnd: TTMSFNCMapsBaseEvent;
    FOnMarkerDragEnd: TTMSFNCMapsBaseEvent;
    FOnMapRenderComplete: TNotifyEvent;
    FClusters: TTMSFNCOpenLayersClusters;
    FOnClusterClick: TTMSFNCMapsBaseEvent;
    function GetOptions: TTMSFNCOpenLayersOptions;
    procedure SetOptions(const Value: TTMSFNCOpenLayersOptions);
    procedure SetTileLayers(const Value: TTMSFNCOpenLayersTileLayers);
    procedure SetPolygons(const Value: TTMSFNCOpenLayersPolygons);
    function GetPolygons: TTMSFNCOpenLayersPolygons;
    function GetPolylines: TTMSFNCOpenLayersPolylines;
    procedure SetPolylines(const Value: TTMSFNCOpenLayersPolylines);
    function GetMarkers: TTMSFNCOpenLayersMarkers;
    procedure SetMarkers(const Value: TTMSFNCOpenLayersMarkers);
    procedure SetClusters(const Value: TTMSFNCOpenLayersClusters);
    function GetHeatMaps: TTMSFNCOpenLayersHeatMaps;
    procedure SetHeatMaps(const Value: TTMSFNCOpenLayersHeatMaps);
  protected
    function GetAPIVersion: string; override;
    function GetDocURL: string; override;
    function GetVersionNr: Integer; override;
    function GetAddOrUpdateTileLayerFunction: string; virtual;
    function GetDeleteTileLayerFunction: string; virtual;
    function GetAddOrUpdateHeatMapFunction: string; virtual;
    function GetDeleteHeatMapFunction: string; virtual;
    function GetOptionsClass: TTMSFNCMapsOptionsClass; override;
    function GetCustomFunctions: string; override;
    function GetCustomGlobalVariables: string; override;
    function GetCustomOptions: string; override;
    function GetAddCustomObjects: string; override;
    function GetCustomMap: string; override;
    function GetMarkersClass: TTMSFNCMapsMarkersClass; override;
    function GetHeatMapsClass: TTMSFNCMapsHeatMapsClass; override;
    function GetPolylinesClass: TTMSFNCMapsPolylinesClass; override;
    function GetPolygonsClass: TTMSFNCMapsPolygonsClass; override;
    function GetRectanglesClass: TTMSFNCMapsRectanglesClass; override;
    function GetCirclesClass: TTMSFNCMapsCirclesClass; override;
    function GetAddOrUpdateClusterFunction: string; virtual;
    function GetDeleteClusterFunction: string; virtual;
    procedure DoPolyElementDragEnd(AEventData: TTMSFNCMapsEventData); virtual;
    procedure DoMarkerDragEnd(AEventData: TTMSFNCMapsEventData); virtual;
    procedure DoMapRenderComplete; virtual;
    procedure DoClusterClick(AEventData: TTMSFNCMapsEventData); virtual;
    procedure CallCustomEvent(AEventData: TTMSFNCMapsEventData); override;
    procedure UpdateTileLayers; virtual;
    procedure DoUpdateTileLayers(const AValue: string); virtual;
    procedure UpdateClusters; virtual;
    procedure DoUpdateClusters(const AValue: string); virtual;
    procedure GetLinks(AList: TTMSFNCMapsLinksList; AIncludeContent: Boolean = True; ACheckReady: Boolean = True); override;
    property Options: TTMSFNCOpenLayersOptions read GetOptions write SetOptions;
    property Clusters: TTMSFNCOpenLayersClusters read FClusters write SetClusters;
    property TileLayers: TTMSFNCOpenLayersTileLayers read FTileLayers write SetTileLayers;
    property Markers: TTMSFNCOpenLayersMarkers read GetMarkers write SetMarkers;
    property HeatMaps: TTMSFNCOpenLayersHeatMaps read GetHeatMaps write SetHeatMaps;
    property Polylines: TTMSFNCOpenLayersPolylines read GetPolylines write SetPolylines;
    property Polygons: TTMSFNCOpenLayersPolygons read GetPolygons write SetPolygons;
    property OnPolyElementDragEnd: TTMSFNCMapsBaseEvent read FOnPolyElementDragEnd write FOnPolyElementDragEnd;
    property OnMarkerDragEnd: TTMSFNCMapsBaseEvent read FOnMarkerDragEnd write FOnMarkerDragEnd;
    property OnMapRenderComplete: TNotifyEvent read FOnMapRenderComplete write FOnMapRenderComplete;
    property OnClusterClick: TTMSFNCMapsBaseEvent read FOnClusterClick write FOnClusterClick;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    procedure EndUpdate; override;

    function AddMarker(ACoordinate: TTMSFNCMapsCoordinateRec; ATitle: string = ''; AIconURL: string = ''): TTMSFNCOpenLayersMarker; reintroduce; overload; virtual;
    function AddMarker(ALatitude: Double; ALongitude: Double; ATitle: string = ''; AIconURL: string = ''): TTMSFNCOpenLayersMarker; reintroduce; overload; virtual;

    function AddPolyline(ACoordinates: TTMSFNCMapsCoordinateRecArray; AClose: Boolean = False): TTMSFNCOpenLayersPolyline; reintroduce; virtual;
    function AddPolygon(ACoordinates: TTMSFNCMapsCoordinateRecArray; AClose: Boolean = False): TTMSFNCOpenLayersPolygon; reintroduce; virtual;
    function AddCircle(ACenter: TTMSFNCMapsCoordinateRec; ARadius: Double = 10000): TTMSFNCOpenLayersCircle; reintroduce; virtual;
    function AddRectangle(ABounds: TTMSFNCMapsBoundsRec): TTMSFNCOpenLayersRectangle; reintroduce; virtual;
    function AddHeatMap(ACoordinates: TTMSFNCMapsCoordinateRecArray): TTMSFNCOpenLayersHeatMap; reintroduce; virtual;

    function AddTileLayer(AURL: string; AOpacity: Single = 1): TTMSFNCOpenLayersTileLayer;
    procedure Clear; override;
    procedure ClearTileLayers;
    procedure ClearClusters;
    procedure ClearHeatMaps;
  end;

  {$IFNDEF LCLLIB}
  {$HINTS OFF}
  {$IF COMPILERVERSION > 22}
  {$IFNDEF LCLLIB}
  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  {$ENDIF}
  {$IFEND}
  {$HINTS ON}
  {$ENDIF}
  TTMSFNCOpenLayers = class(TTMSFNCCustomOpenLayers)
  protected
    procedure RegisterRuntimeClasses; override;
    procedure GetHeadLinks(AList: TTMSFNCMapsLinksList; ACheckReady: Boolean = True); override;
  public
    procedure Navigate; overload; override;
    procedure Navigate(const AURL: string); overload; override;
    procedure ExecuteJavaScript(AScript: String; ACompleteEvent: TTMSFNCWebBrowserJavaScriptCompleteEvent = nil; AImmediate: Boolean = False); override;
    function ExecuteJavaScriptSync(AScript: String): string; override;
    procedure LoadHTML(AHTML: String); override;
    procedure LoadFile(AFile: String); override;
    procedure Initialize; override;
    procedure DeInitialize; override;
    procedure GoForward; override;
    procedure GoBack; override;
    procedure Reload; override;
    procedure StopLoading; override;
    procedure AddBridge(ABridgeName: string; ABridgeObject: TObject); override;
    procedure RemoveBridge(ABridgeName: string); override;
    procedure CaptureScreenShot(ACompleteEvent: TTMSFNCWebBrowserCaptureScreenShotCompleteEvent = nil); override;
    function GetBridgeCommunicationLayer(ABridgeName: string): string; override;
    function NativeEnvironment: Pointer; override;
    function NativeBrowser: Pointer; override;
    function IsFMXBrowser: Boolean; override;
    function CanGoBack: Boolean; override;
    function CanGoForward: Boolean; override;
    {$IFDEF ANDROID}
    function NativeDialog: Pointer; override;
    {$ENDIF}
    {$IFDEF MSWINDOWS}
    function GetWebBrowserInstance: IInterface; override;
    {$ENDIF}
    property OnCloseForm;
    property EnableContextMenu;
    property EnableShowDebugConsole;
    property EnableAcceleratorKeys;
    property CacheFolder;
    property CacheFolderName;
    property AutoClearCache;
    procedure ClearCache; override;
    procedure ShowDebugConsole; override;

    property MapsInstance;
    property MapsProperties;
  published
    property OnCustomizeLocalAccessFileName;
    property OnCaptureScreenShot;
    property OnCustomizeCSS;
    property OnCustomizeHeadLinks;
    property OnCustomizeJavaScript;
    property OnCustomizeMap;
    property OnCustomizeGlobalVariables;
    property OnCustomizeMarker;
    property OnCustomizePopup;
    property OnCustomizeOptions;
    property OnCustomizePolyElement;
    property OnGetDefaultHTMLMessage;
    property OnZoomChanged;
    property OnMapCenterChanged;
    property OnMapTypeChanged;
    property OnMapMoveStart;
    property OnMapMoveEnd;
    property OnMapClick;
    property OnMapRightClick;
    property OnMapDblClick;
    property OnMapMouseUp;
    property OnMapMouseDown;
    property OnMapMouseMove;
    property OnMapMouseEnter;
    property OnMapMouseLeave;
    property OnMapRenderComplete;
    property OnMarkerDragEnd;
    property OnMarkerClick;
    property OnMarkerRightClick;
    property OnMarkerDblClick;
    property OnMarkerMouseUp;
    property OnMarkerMouseDown;
    property OnMarkerMouseEnter;
    property OnMarkerMouseLeave;
    property OnPolyElementDragEnd;
    property OnPolyElementClick;
    property OnPolyElementRightClick;
    property OnPolyElementDblClick;
    property OnPolyElementMouseUp;
    property OnPolyElementMouseDown;
    property OnPolyElementMouseEnter;
    property OnPolyElementMouseLeave;
    property OnCustomEvent;
    property OnMapInitialized;
    property OnGetCenterCoordinate;
    property OnGetZoomLevel;
    property OnGetBounds;
    property OnCreateGPXTrack;
    property OnCreateGPXSegment;
    property OnCreateGeoJSONObject;
    property OnClusterClick;

    property Polylines;
    property Polygons;
    property Circles;
    property Rectangles;
    property Markers;
    property Options;
    property Version;
    property LocalFileAccess;
    property LibraryLocation;
    property TileLayers;
    property RouteCalculator;
    property ElementContainers;
    property HeadLinks;
    property DesigntimeEnabled;
    property Clusters;
    property HeatMaps;
  end;

implementation

{$R TMSFNCMapsOpenLayers.res}

uses
  {%H-}Math,
  WEBLib.TMSFNCPersistence,
  WEBLib.TMSFNCUtils,
  WEBLib.TMSFNCMaps.OpenLayers,
  SysUtils
  {$IFDEF FMXLIB}
  ,FMX.Types, IOUtils
  {$ENDIF}
  {$IFDEF VCLLIB}
  ,IOUtils
  {$ENDIF}
  ;

const
  TILELAYERVAR = 'tileLayer';
  TILELAYERARRAYVAR = 'tileLayerArray';
  TILESERVERVAR = 'tileServer';
  GETTILELAYERARRAYVAR = 'get' + TILELAYERARRAYVAR + '()';
  ADDORUPDATETILELAYERFUNCTION = 'addOrUpdateTileLayer';
  DELETETILELAYERFUNCTION = 'deleteTileLayer';

  GEOTIFFLAYERSCRIPT = OPENLAYERSAPIURL + '/geotiff/dist/geotiff.bundle.min.js';


{ TTMSFNCCustomOpenLayers }

constructor TTMSFNCCustomOpenLayers.Create(AOwner: TComponent);
{$IFNDEF WEBLIB}
var
  f: string;
  r: TResourceStream;
{$ENDIF}
begin
  inherited;
  FTileLayers := TTMSFNCOpenLayersTileLayers.Create(Self);
  FClusters := TTMSFNCOpenLayersClusters.Create(Self);
  LibraryLocation := llOnline;
  Service := msOpenLayers;

  LocalFileAccess := true;

  if csDesigning in ComponentState then
    Exit;

  {$IFNDEF LCLWEBLIB}
  TDirectory.CreateDirectory(TTMSFNCUtils.AddBackslash(TTMSFNCUtils.GetTempPath) + 'ttmsfncopenlayers');
  {$ENDIF}
  {$IFDEF LCLLIB}
  CreateDir(TTMSFNCUtils.AddBackslash(TTMSFNCUtils.GetTempPath) + 'ttmsfncopenlayers');
  {$ENDIF}

  {$IFNDEF WEBLIB}
  f := TTMSFNCUtils.AddBackslash(TTMSFNCUtils.GetTempPath) + 'ttmsfncopenlayers\ol.js';
  r := TTMSFNCUtils.GetResourceStream('OL_JS', HInstance);

  try
    r.SaveToFile(f);
  finally
    r.Free;
  end;

  f := TTMSFNCUtils.AddBackslash(TTMSFNCUtils.GetTempPath) + 'ttmsfncopenlayers\ol.css';
  r := TTMSFNCUtils.GetResourceStream('OL_CSS', HInstance);

  try
    r.SaveToFile(f);
  finally
    r.Free;
  end;
  {$ENDIF}
end;

function TTMSFNCCustomOpenLayers.GetOptionsClass: TTMSFNCMapsOptionsClass;
begin
  Result := TTMSFNCOpenLayersOptions;
end;

function TTMSFNCCustomOpenLayers.GetPolygons: TTMSFNCOpenLayersPolygons;
begin
  Result := TTMSFNCOpenLayersPolygons(inherited Polygons);
end;

function TTMSFNCCustomOpenLayers.GetPolygonsClass: TTMSFNCMapsPolygonsClass;
begin
  Result := TTMSFNCOpenLayersPolygons;
end;

function TTMSFNCCustomOpenLayers.GetPolylines: TTMSFNCOpenLayersPolylines;
begin
  Result := TTMSFNCOpenLayersPolylines(inherited Polylines);
end;

function TTMSFNCCustomOpenLayers.GetPolylinesClass: TTMSFNCMapsPolylinesClass;
begin
  Result := TTMSFNCOpenLayersPolylines;
end;

function TTMSFNCCustomOpenLayers.GetRectanglesClass: TTMSFNCMapsRectanglesClass;
begin
  Result := TTMSFNCOpenLayersRectangles;
end;

destructor TTMSFNCCustomOpenLayers.Destroy;
begin
  FTileLayers.Free;
  FClusters.Free;
  inherited;
end;

procedure TTMSFNCCustomOpenLayers.DoUpdateClusters(const AValue: string);
var
  I, j: Integer;
  ma: string;
  sl: TStringList;
  m: TTMSFNCOpenLayersMarkersList;
begin
  if not MapInitialized or IsDestroying or (UpdateCount > 0) then
    Exit;

  ma := ParseScript(AValue);

  sl := TStringList.Create;
  try
    TTMSFNCUtils.Split(',', ma, sl);
    for I := 0 to Clusters.Count - 1 do
    begin
      j := sl.IndexOf(Clusters[I].ID);
      if (j = -1) or (Clusters[I].FReload) then
      begin
        Clusters[I].FReload := False;
        m := Clusters[I].Markers;
        ExecuteJavaScript(ADDORUPDATECLUSTERFUNCTION + '([' + Clusters[I].ToJSON + ', ' + m.ToJSON + ']);');
      end;
      if j <> -1 then
        sl.Delete(j);
    end;

    for I := 0 to sl.Count - 1 do
      ExecuteJavaScript(DELETECLUSTERFUNCTION + '({"ID": "' + sl[I] + '"})');

  finally
    sl.Free;
  end;
end;

procedure TTMSFNCCustomOpenLayers.DoUpdateTileLayers(const AValue: string);
var
  I, j: Integer;
  ma: string;
  sl: TStringList;
begin
  if not MapInitialized or IsDestroying or (UpdateCount > 0) then
    Exit;

  ma := ParseScript(AValue);

  sl := TStringList.Create;
  try
    TTMSFNCUtils.Split(',', ma, sl);
    for I := 0 to TileLayers.Count - 1 do
    begin
      j := sl.IndexOf(TileLayers[I].ID);
      ExecuteJavaScript(ADDORUPDATETILELAYERFUNCTION + '(' + TileLayers[I].ToJSON + ');');
      if j <> -1 then
        sl.Delete(j);
    end;

    for I := 0 to sl.Count - 1 do
      ExecuteJavaScript(DELETETILELAYERFUNCTION + '({"ID": "' + sl[I] + '"})');

  finally
    sl.Free;
  end;
end;

procedure TTMSFNCCustomOpenLayers.EndUpdate;
begin
  inherited;
  if UpdateCount = 0 then
  begin
    UpdateTileLayers;
    UpdateClusters;
    UpdateHeatMaps;
  end;
end;

function TTMSFNCCustomOpenLayers.GetCustomMap: string;
var
  TileServer: string;
begin
  if not Options.ShowBaseLayer then
    TileServer := ''
  else
    TileServer := Options.TileServer;

//  if (Options.TileServer <> '') then
//    Result :=
//      '  ' + MAPVAR + '.getLayers().getArray()[0].setSource(new ' + MAPSERVICEVAR + '.source.XYZ({url: "' + TileServer + '"}));' + LB
//  else
//    Result :=
//      '  ' + MAPVAR + '.getLayers().getArray()[0].setSource(new ' + MAPSERVICEVAR + MAPSOURCEVAR + ');' + LB;
end;

function TTMSFNCCustomOpenLayers.GetCustomOptions: string;
begin
  Result :=
    'var TileServer = ' + PARAMSNAME + '["TileServer"];' + LB +
    'if (!' + PARAMSNAME + '["ShowBaseLayer"]) {' + LB +
    '  TileServer = " ";' + LB +
    '}' + LB +
    'if (TileServer != "" && TileServer != ' + TILESERVERVAR + '){' + LB +
    '  ' + MAPVAR + '.getLayers().getArray()[0].setSource(new ' + MAPSERVICEVAR + '.source.XYZ({url: TileServer}));' + LB +
    '} else if (TileServer == "" && TileServer != ' + TILESERVERVAR + '){' + LB +
    '  ' + MAPVAR + '.getLayers().getArray()[0].setSource(new ' + MAPSERVICEVAR + MAPSOURCEVAR + ');' + LB +
    '}' + LB +

    TILESERVERVAR + ' = TileServer;' + LB;
end;

function TTMSFNCCustomOpenLayers.GetAddOrUpdateClusterFunction: string;
begin
  Result := '';
  if not Assigned(MapsInstance) then
    Exit;

  Result :=
    'function ' + ADDORUPDATECLUSTERFUNCTION + '(' + PARAMSNAME + '){' + LB +

    TryCatchStart +

    '  var ' + CLUSTERVAR + ' = ' + CLUSTERARRAYVAR + '[' + PARAMSNAME + '[0]["ID"]];' + LB +

    '  var m = [];' + LB +
    '  for (var k in ' + PARAMSNAME + '[1]){' + LB +
    '    var mi = ' + PARAMSNAME + '[1][k];' + LB +
    '    layer = ' + MARKERARRAYVAR + '[mi["ID"]];' + LB +
    '    layer.setVisible(false);' + LB +
    '    var feature = layer.getSource().getFeatures()[0];' + LB +
    '    m.push(feature);' + LB +
    '  }' + LB +

    '  if (!' + CLUSTERVAR + '){' + LB +

    '    var source = new ' + MAPSERVICEVAR + '.source.Vector({' + LB +
    '      features: m' + LB +
    '    });' + LB +

    '    var clusterSource = new ' + MAPSERVICEVAR + '.source.Cluster({' + LB +
    '      distance: 40,' + LB +
    '      source: source' + LB +
    '    });' + LB +

    '    var styleCache = {};' + LB +
    '    var cimage = {};' + LB +

    '    if (' + PARAMSNAME + '[0]["ImagePath"] != ""){' + LB +
    '      cimage = new ' + MAPSERVICEVAR + '.style.Icon({' + LB +
    '        src: ' + PARAMSNAME + '[0]["ImagePath"]' + LB +
    '      });' + LB +
    '    } else {' + LB +
    '      cimage = new ' + MAPSERVICEVAR + '.style.Circle({' + LB +
    '        radius: 15,' + LB +
    '        stroke: new ' + MAPSERVICEVAR + '.style.Stroke({' + LB +
    '          color: ' + PARAMSNAME + '[0]["StrokeColor"]' + LB +
    '        }),' + LB +
    '        fill: new ' + MAPSERVICEVAR + '.style.Fill({' + LB +
    '          color: ' + PARAMSNAME + '[0]["FillColor"]' + LB +
    '        })' + LB +
    '      });' + LB +
    '    }' + LB +

    '    var ' + CLUSTERVAR + ' = new ' + MAPSERVICEVAR + '.layer.Vector({' + LB +
    '      zIndex: 1,' + LB +
    '      source: clusterSource,' + LB +
    '      style: function(feature, resolution) {' + LB +
    '        var size = feature.get(''features'').length;' + LB +
    '        var style = styleCache[size];' + LB +
    '        if (!style) {' + LB +
    '          style = [new ' + MAPSERVICEVAR + '.style.Style({' + LB +
    '            image: cimage,' + LB +
    '            text: new ' + MAPSERVICEVAR + '.style.Text({' + LB +
    '              text: size.toString(),' + LB +
    '              fill: new ' + MAPSERVICEVAR + '.style.Fill({' + LB +
    '                color: ' + PARAMSNAME + '[0]["FontColor"]' + LB +
    '              })' + LB +
    '            })' + LB +

    '          })];' + LB +
    '          styleCache[size] = style;' + LB +
    '        }' + LB +
    '        return style;' + LB +
    '      }' + LB +
    '    });' + LB +

    '    ' + MAPVAR + '.addLayer('+ CLUSTERVAR +');' + LB +

    '  };';


  Result := Result +
    '  if (' + CLUSTERVAR + '){' + LB +
    '    ' + CLUSTERARRAYVAR + '[' + PARAMSNAME + '[0]["ID"]] = ' + CLUSTERVAR + ';' + LB +

    '    ' + CLUSTERVAR + '.ClusterClick = ClusterClick;' + LB +
    '    var ClusterClick = function(event){' + LB +
    '        ' + GETSENDEVENT + '(parseEvent(event, "ClusterClick", ' + PARAMSNAME + '[0]["ID"]));' + LB +
    '    }' + LB + LB +
    '    ' + MAPVAR + '.on(''click'', ClusterClick);' + LB +

    '  }' + LB +

    TryCatchEnd('TMS FNC OpenLayers Marker Cluster error') +
    '}';

end;

function TTMSFNCCustomOpenLayers.GetAddOrUpdateHeatMapFunction: string;
begin
  Result := '';
  if not Assigned(MapsInstance) then
    Exit;

  Result :=
    'function ' + ADDORUPDATEHEATMAPFUNCTION + '(' + PARAMSNAME + '){' + LB +

    TryCatchStart +

    '  var heatmapData = new ol.source.Vector();' + LB +
    '  var coord;' + LB +
    '  var lonLat;' + LB +
    '  var pointFeature;' + LB +

    '  if (!(typeof ' + PARAMSNAME + '["WeightedCoordinates"] == "undefined")) {' + LB +
    '    for (I = 0; I < ' + PARAMSNAME + '["WeightedCoordinates"].length; I++){' + LB +
    '      var lat = ' + PARAMSNAME + '["WeightedCoordinates"][I]["Coordinate"]["Latitude"];' + LB +
    '      var lng = ' + PARAMSNAME + '["WeightedCoordinates"][I]["Coordinate"]["Longitude"];' + LB +
    '      var cw = ' + PARAMSNAME + '["WeightedCoordinates"][I]["Weight"];' + LB +
    '      if (cw < 1) { cw = 1; }' + LB +
    '      coord = ol.proj.fromLonLat([lng, lat]);' + LB +
    '      lonLat = new ol.geom.Point(coord);' + LB +
    '      pointFeature = new ol.Feature({' + LB +
    '        geometry: lonLat,' + LB +
    '        weight: cw' + LB +
    '      });' + LB +
    '      heatmapData.addFeature(pointFeature);' + LB +
    '    }'+ LB +
    '  }'+ LB +

    '  var ' + HEATMAPVAR + ' = ' + HEATMAPARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '  ' + HEATMAPVAR + ' = new ' + MAPSERVICEVAR + '.layer.Heatmap({' + LB +
    '    source: heatmapData,' + LB +
    '    opacity: ' + PARAMSNAME + '["Opacity"],' + LB +
    '    gradient: [toRGBA(' + PARAMSNAME + '["GradientStartColor"], 1), toRGBA(' + PARAMSNAME + '["GradientMidColor"], 1), toRGBA(' + PARAMSNAME + '["GradientEndColor"], 1)]' + LB +
    '  });' + LB +
    '  map.addLayer(' + HEATMAPVAR + ');' + LB +

    '  if (' + HEATMAPVAR + '){' + LB +
    '    ' + HEATMAPARRAYVAR + '[' + PARAMSNAME + '["ID"]] = ' + HEATMAPVAR + ';' + LB +
    '  }' + LB +

    TryCatchEnd('TMS FNC OpenLayers HeatMap error') +
    '}';
end;

function TTMSFNCCustomOpenLayers.GetDeleteHeatMapFunction: string;
begin
  Result := '';
  if not Assigned(MapsInstance) then
    Exit;

  Result :=
    'function ' + DELETEHEATMAPFUNCTION + '(' + PARAMSNAME + '){' + LB +
    TryCatchStart +
    '  var ' + HEATMAPVAR + ' = ' + HEATMAPARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '  if (' + HEATMAPVAR + '){' + LB +
    '    map.removeLayer(' + HEATMAPVAR + ');' + LB +
    '    delete ' + HEATMAPARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '    ' + HEATMAPVAR + ' = null;' + LB +
    '  }' + LB +
    TryCatchEnd('TMS FNC OpenLayers delete HeatMap error') +
    '}';
end;

function TTMSFNCCustomOpenLayers.GetAddOrUpdateTileLayerFunction: string;
begin
  Result := '';
  if not Assigned(MapsInstance) then
    Exit;

  Result :=
    'function ' + ADDORUPDATETILELAYERFUNCTION + '(' + PARAMSNAME + '){' + LB +
    TryCatchStart +
    '  var ' + TILELAYERVAR + ' = ' + TILELAYERARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '  var l;' + LB +
    '  var s;' + LB +

    '  switch(' + PARAMSNAME + '["Source"]){' + LB +

    //XYZ
    '    case 0:' + LB +
    '    s = new ' + MAPSERVICEVAR + '.source.XYZ({' + LB +
    '        url: ' + PARAMSNAME + '["URL"]' + LB +
    '      });' + LB +

    '    l = new ' + MAPSERVICEVAR + '.layer.Tile({' + LB +
    '      name: ' + PARAMSNAME + '["ID"],' + LB +
    '      className: ' + PARAMSNAME + '["CSSClassName"],' + LB +
    '      opacity: ' + PARAMSNAME + '["Opacity"]' + ',' + LB +
    '      source: s' + LB +
    '    });' + LB +

    '     break;' + LB +

    //WMS
    '    case 1:' + LB +
    '    s = new ' + MAPSERVICEVAR + '.source.TileWMS({' + LB +
    '        url: ' + PARAMSNAME + '["URL"],' + LB +
    '        params: {"LAYERS":' + PARAMSNAME + '["Params"]["Layers"]},' + LB +
    '      });' + LB +

    '    l = new ' + MAPSERVICEVAR + '.layer.Tile({' + LB +
    '      name: ' + PARAMSNAME + '["ID"],' + LB +
    '      className: ' + PARAMSNAME + '["CSSClassName"],' + LB +
    '      opacity: ' + PARAMSNAME + '["Opacity"]' + ',' + LB +
    '      source: s' + LB +
    '    });' + LB +

    '     break;' + LB +

    //JSON
    '    case 2:' + LB +

    '    const style = new ' + MAPSERVICEVAR + '.style.Style({' + LB +
    '      fill: new ' + MAPSERVICEVAR + '.style.Fill({' + LB +
    '        color: "#eeeeee",' + LB +
    '      }),' + LB +
    '    });' + LB +

    '    s = new ' + MAPSERVICEVAR + '.source.Vector({' + LB +
    '      url: ' + PARAMSNAME + '["URL"],' + LB +
    '      format: new ' + MAPSERVICEVAR + '.format.GeoJSON(),' + LB +
    '    });' + LB +

    '    l = new ' + MAPSERVICEVAR + '.layer.Vector({' + LB +
    '      background: "#1a2b39",' + LB +
    '      name: ' + PARAMSNAME + '["ID"],' + LB +
    '      className: ' + PARAMSNAME + '["CSSClassName"],' + LB +
    '      source: s,' + LB +
    '      opacity: ' + PARAMSNAME + '["Opacity"]' + ',' + LB +
    '      style: function (feature) {' + LB +
    '        style.getFill().setColor(feature.get("COLOR"));' + LB +
    '        return style;' + LB +
    '      },' + LB +
    '    });' + LB +

    '     break;' + LB +

    //GeoTiff
    '   case 3:' + LB +

    '   s = new ' + MAPSERVICEVAR + '.source.GeoTIFF({' + LB +
    '      sources: [' + LB +
    '        {' + LB +
    '          url: ' + PARAMSNAME + '["URL"],' + LB +
    '        },' + LB +
    '      ],' + LB +
    '      interpolate: false,' + LB +
    '    });' + LB +

    '    l = new ' + MAPSERVICEVAR + '.layer.WebGLTile({' + LB +
    '      name: ' + PARAMSNAME + '["ID"],' + LB +
    '      className: ' + PARAMSNAME + '["CSSClassName"],' + LB +
    '      source: s,' + LB +
    '    });' + LB +

    '     break;' + LB +
    '  }' + LB;

  Result := Result + '' + LB +
    '  if (!' + TILELAYERVAR + '){ ' + LB +
    '  ' + MAPVAR + '.addLayer(l);' + LB +
    '  ' + TILELAYERVAR + ' = true;' + LB +
    ' }' + LB +
    '  if (' + TILELAYERVAR + '){' + LB +
    '    ' + TILELAYERARRAYVAR + '[' + PARAMSNAME + '["ID"]] = ' + TILELAYERVAR + ';' + LB +
    '    ' + MAPVAR + '.getLayers().forEach(layer => {' + LB +
    '      if (layer && layer.get("name") === ' + PARAMSNAME + '["ID"]) {' + LB +
    '        layer.setOpacity(' + PARAMSNAME + '["Opacity"]);' + LB +
    '        layer.setSource(s);' + LB +
    '      }' + LB +
    '    });' + LB +
    '  }' + LB +

    TryCatchEnd('TMS FNC OpenLayers TileLayer error') +
    '}';
end;

function TTMSFNCCustomOpenLayers.GetAPIVersion: string;
begin
  Result := Options.Version;
end;

function TTMSFNCCustomOpenLayers.GetDeleteClusterFunction: string;
begin
  Result := '';
  if not Assigned(MapsInstance) then
    Exit;

  Result :=
    'function ' + DELETECLUSTERFUNCTION + '(' + PARAMSNAME + '){' + LB +
    TryCatchStart +
    '  var ' + CLUSTERVAR + ' = ' + CLUSTERARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '  if (' + CLUSTERVAR + '){' + LB +
    '    ' + MAPVAR + '.removeLayer(' + CLUSTERVAR + ');' + LB +
    '    delete ' + CLUSTERARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '    ' + CLUSTERVAR + ' = null;' + LB +
    '  }' + LB +
    TryCatchEnd('TMS FNC OpenLayers delete Marker Cluster error') +
    '}';
end;

function TTMSFNCCustomOpenLayers.GetDeleteTileLayerFunction: string;
begin
  Result := '';
  if not Assigned(MapsInstance) then
    Exit;

  Result :=
    'function ' + DELETETILELAYERFUNCTION + '(' + PARAMSNAME + '){' + LB +
    TryCatchStart +
    '  var ' + TILELAYERVAR + ' = ' + TILELAYERARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '  if (' + TILELAYERVAR + '){' + LB +
    '    ' + MAPVAR + '.getLayers().forEach(layer => {' + LB +
    '      if (layer && layer.get("name") === ' + PARAMSNAME + '["ID"]) {' + LB +
    '        ' + MAPVAR + '.removeLayer(layer);' + LB +
    '      }' + LB +
    '    });' + LB +

    '    delete ' + TILELAYERARRAYVAR + '[' + PARAMSNAME + '["ID"]];' + LB +
    '    ' + TILELAYERVAR + ' = null;' + LB +
    '  }' + LB +
    TryCatchEnd('TMS FNC OpenLayers delete TileLayer error') +
    '}';
end;

function TTMSFNCCustomOpenLayers.GetDocURL: string;
begin
  Result := TTMSFNCBaseDocURL + 'tmsfncmaps/components/ttmsfncmaps/#ttmsfncopenlayers';
end;

function TTMSFNCCustomOpenLayers.GetHeatMaps: TTMSFNCOpenLayersHeatMaps;
begin
  Result := TTMSFNCOpenLayersHeatMaps(inherited HeatMaps);
end;

function TTMSFNCCustomOpenLayers.GetHeatMapsClass: TTMSFNCMapsHeatMapsClass;
begin
  Result := TTMSFNCOpenLayersHeatMaps;
end;

procedure TTMSFNCCustomOpenLayers.GetLinks(AList: TTMSFNCMapsLinksList;
  AIncludeContent, ACheckReady: Boolean);
begin
  inherited;
  AList.Insert(0, TTMSFNCMapsLink.CreateScript(GEOTIFFLAYERSCRIPT, 'text/JavaScript', '', ''));
end;

function TTMSFNCCustomOpenLayers.GetMarkers: TTMSFNCOpenLayersMarkers;
begin
  Result := TTMSFNCOpenLayersMarkers(inherited Markers);
end;

function TTMSFNCCustomOpenLayers.GetMarkersClass: TTMSFNCMapsMarkersClass;
begin
  Result := TTMSFNCOpenLayersMarkers;
end;

function TTMSFNCCustomOpenLayers.GetAddCustomObjects: string;
var
  I: Integer;
begin
  Result := '';
  for I := 0 to TileLayers.Count - 1 do
    Result := Result + '  ' + ADDORUPDATETILELAYERFUNCTION + '(' + TileLayers[I].ToJSON + ');' + LB;

  for I := 0 to HeatMaps.Count - 1 do
    Result := Result + '  ' + ADDORUPDATEHEATMAPFUNCTION + '(' + HeatMaps[I].ToJSON + ');' + LB;

  for I := 0 to Clusters.Count - 1 do
    Result := Result + '  ' + ADDORUPDATECLUSTERFUNCTION + '([' + Clusters[I].ToJSON + ', []]);' + LB;
end;

function TTMSFNCCustomOpenLayers.GetCustomGlobalVariables: string;
begin
  Result :=
    'var ' + TILELAYERVAR + ';' + LB +
    'var ' + TILELAYERARRAYVAR + ' = {};' + LB +
    'var ' + TILESERVERVAR + ' = "";' + LB +
    'function ' + GETTILELAYERARRAYVAR + '{' + LB +
    '  var arr = [];' + LB +
    '  for (var key in ' + TILELAYERARRAYVAR + '){' + LB +
    '    var v = key;' + LB +
    '    arr.push(v);' + LB +
    '  }' + LB +
    '  return arr.toString();' + LB +
    '}' + LB +
    'var ' + HEATMAPVAR + ';' + LB +
    'var ' + HEATMAPARRAYVAR + ' = {};' + LB +
    'function ' + GETHEATMAPARRAYVAR + '{' + LB +
    '  var arr = [];' + LB +
    '  for (var key in ' + HEATMAPARRAYVAR + '){' + LB +
    '    var v = key;' + LB +
    '    arr.push(v);' + LB +
    '  }' + LB +
    '  return arr.toString();' + LB +
    '}';
end;

function TTMSFNCCustomOpenLayers.GetCirclesClass: TTMSFNCMapsCirclesClass;
begin
  Result := TTMSFNCOpenLayersCircles;
end;

function TTMSFNCCustomOpenLayers.GetCustomFunctions: string;
begin
  Result := GetAddOrUpdateTileLayerFunction + LB + LB +
  GetDeleteTileLayerFunction + LB + LB +
  GetAddOrUpdateHeatMapFunction + LB + LB +
  GetDeleteHeatMapFunction + LB + LB +
  GetAddOrUpdateClusterFunction + LB + LB +
  GetDeleteClusterFunction;
end;

function TTMSFNCCustomOpenLayers.GetOptions: TTMSFNCOpenLayersOptions;
begin
  Result := inherited Options as TTMSFNCOpenLayersOptions;
end;

function TTMSFNCCustomOpenLayers.GetVersionNr: Integer;
begin
  Result := MakeLong(MakeWord(BLD_VER,REL_VER),MakeWord(MIN_VER,MAJ_VER));
end;

procedure TTMSFNCCustomOpenLayers.SetClusters(
  const Value: TTMSFNCOpenLayersClusters);
begin
  FClusters.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.SetHeatMaps(
  const Value: TTMSFNCOpenLayersHeatMaps);
begin
  HeatMaps.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.SetMarkers(
  const Value: TTMSFNCOpenLayersMarkers);
begin
  Markers.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.SetOptions(
  const Value: TTMSFNCOpenLayersOptions);
begin
  Options.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.SetPolygons(
  const Value: TTMSFNCOpenLayersPolygons);
begin
  Polygons.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.SetPolylines(
  const Value: TTMSFNCOpenLayersPolylines);
begin
  Polylines.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.SetTileLayers(
  const Value: TTMSFNCOpenLayersTileLayers);
begin
  FTileLayers.Assign(Value);
end;

procedure TTMSFNCCustomOpenLayers.UpdateClusters;
begin
  if not MapInitialized or IsDestroying or (UpdateCount > 0) then
    Exit;

  ExecuteJavaScript(GETCLUSTERARRAYVAR, {$IFDEF LCLWEBLIB}@{$ENDIF}DoUpdateClusters);
end;

procedure TTMSFNCCustomOpenLayers.UpdateTileLayers;
begin
  if not MapInitialized or IsDestroying or (UpdateCount > 0) then
    Exit;

  ExecuteJavaScript(GETTILELAYERARRAYVAR, {$IFDEF LCLWEBLIB}@{$ENDIF}DoUpdateTileLayers);
end;

function TTMSFNCCustomOpenLayers.AddMarker(
  ACoordinate: TTMSFNCMapsCoordinateRec; ATitle,
  AIconURL: string): TTMSFNCOpenLayersMarker;
begin
  Result := TTMSFNCOpenLayersMarker(inherited AddMarker(ACoordinate, ATitle, AIconURL));
end;

function TTMSFNCCustomOpenLayers.AddCircle(ACenter: TTMSFNCMapsCoordinateRec;
  ARadius: Double): TTMSFNCOpenLayersCircle;
begin
  Result := TTMSFNCOpenLayersCircle(inherited AddCircle(ACenter, ARadius));
end;

function TTMSFNCCustomOpenLayers.AddHeatMap(
  ACoordinates: TTMSFNCMapsCoordinateRecArray): TTMSFNCOpenLayersHeatMap;
begin
  Result := TTMSFNCOpenLayersHeatMap(inherited AddHeatMap(ACoordinates));
end;

function TTMSFNCCustomOpenLayers.AddMarker(ALatitude, ALongitude: Double;
  ATitle, AIconURL: string): TTMSFNCOpenLayersMarker;
begin
  Result := TTMSFNCOpenLayersMarker(inherited AddMarker(ALatitude, ALongitude, ATitle, AIconURL));
end;

function TTMSFNCCustomOpenLayers.AddPolygon(
  ACoordinates: TTMSFNCMapsCoordinateRecArray;
  AClose: Boolean): TTMSFNCOpenLayersPolygon;
begin
  Result := TTMSFNCOpenLayersPolygon(inherited AddPolygon(ACoordinates, AClose));
end;

function TTMSFNCCustomOpenLayers.AddPolyline(
  ACoordinates: TTMSFNCMapsCoordinateRecArray;
  AClose: Boolean): TTMSFNCOpenLayersPolyline;
begin
  Result := TTMSFNCOpenLayersPolyline(inherited AddPolyline(ACoordinates, AClose));
end;

function TTMSFNCCustomOpenLayers.AddRectangle(
  ABounds: TTMSFNCMapsBoundsRec): TTMSFNCOpenLayersRectangle;
begin
  Result := TTMSFNCOpenLayersRectangle(inherited AddRectangle(ABounds));
end;

function TTMSFNCCustomOpenLayers.AddTileLayer(
  AURL: string; AOpacity: Single = 1): TTMSFNCOpenLayersTileLayer;
begin
  BeginUpdate;
  Result := TileLayers.Add;
  Result.FURL := AURL;
  Result.FOpacity := AOpacity;
  EndUpdate;
end;

procedure TTMSFNCCustomOpenLayers.DoClusterClick(
  AEventData: TTMSFNCMapsEventData);
begin
  if Assigned(OnClusterClick) then
    OnClusterClick(Self, AEventData);
end;

procedure TTMSFNCCustomOpenLayers.DoMapRenderComplete;
begin
  if Assigned(OnMapRenderComplete) then
    OnMapRenderComplete(Self);
end;

procedure TTMSFNCCustomOpenLayers.DoMarkerDragEnd(
  AEventData: TTMSFNCMapsEventData);
begin
  if Assigned(OnMarkerDragEnd) then
    OnMarkerDragEnd(Self, AEventData);
end;

procedure TTMSFNCCustomOpenLayers.DoPolyElementDragEnd(
  AEventData: TTMSFNCMapsEventData);
begin
  if Assigned(OnPolyElementDragEnd) then
    OnPolyElementDragEnd(Self, AEventData);
end;

procedure TTMSFNCCustomOpenLayers.CallCustomEvent(
  AEventData: TTMSFNCMapsEventData);
begin
  inherited;
  if AEventData.EventName = 'MarkerDragEnd' then
    DoMarkerDragEnd(AEventData);

  if AEventData.EventName = 'PolyElementDragEnd' then
    DoPolyElementDragEnd(AEventData);

  if AEventData.EventName = 'MapRenderComplete' then
    DoMapRenderComplete;

  if AEventData.EventName = 'ClusterClick' then
    DoClusterClick(AEventData);
end;

procedure TTMSFNCCustomOpenLayers.Clear;
begin
  inherited;
  BeginUpdate;
  ClearTileLayers;
  ClearClusters;
  EndUpdate;
end;

procedure TTMSFNCCustomOpenLayers.ClearClusters;
begin
  BeginUpdate;
  Clusters.Clear;
  EndUpdate;
end;

procedure TTMSFNCCustomOpenLayers.ClearHeatMaps;
begin
  BeginUpdate;
  HeatMaps.Clear;
  EndUpdate;
end;

procedure TTMSFNCCustomOpenLayers.ClearTileLayers;
begin
  BeginUpdate;
  TileLayers.Clear;
  EndUpdate;
end;

constructor TTMSFNCOpenLayersOptions.Create;
begin
  inherited;
  TileServer := '';
  ShowBaseLayer := True;
  Version := 'v10.7.0'
end;

procedure TTMSFNCOpenLayersOptions.SetShowBaseLayer(const Value: Boolean);
begin
  if FShowBaseLayer <> Value then
  begin
    FShowBaseLayer := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersOptions.SetTileServer(const Value: string);
begin
  if FTileServer <> Value then
  begin
    FTileServer := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersOptions.SetVersion(const Value: string);
begin
  if FVersion <> Value then
  begin
    FVersion := Value;
    Changed;
  end;
end;

{ TTMSFNCOpenLayersTileLayer }

procedure TTMSFNCOpenLayersTileLayer.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCOpenLayersTileLayer then
  begin
    FURL := (Source as TTMSFNCOpenLayersTileLayer).URL;
    FOpacity := (Source as TTMSFNCOpenLayersTileLayer).Opacity;
    FParams.Assign((Source as TTMSFNCOpenLayersTileLayer).Params);
    FSource := (Source as TTMSFNCOpenLayersTileLayer).Source;
//    FType := (Source as TTMSFNCOpenLayersTileLayer).&Type;
    FCSSClassName := (Source as TTMSFNCOpenLayersTileLayer).CSSClassName;
  end;
end;

constructor TTMSFNCOpenLayersTileLayer.Create(ACollection: TCollection);
begin
  inherited;

  FParams := TTMSFNCOpenLayersTileLayerParams.Create;
  FParams.OnChange := @DoParamsChanged;
//  FType := tltTile;

  FURL := '';
  FOpacity := 1;
  FSource := tlsXYZ;
  FCSSClassName := '';

  if Assigned(ACollection) then
    FOwner := (Collection as TTMSFNCOpenLayersTileLayers).FOwner;

  UpdateTileLayer;
end;

destructor TTMSFNCOpenLayersTileLayer.Destroy;
begin
  FParams.Free;
  inherited;
  UpdateTileLayer;
end;

procedure TTMSFNCOpenLayersTileLayer.DoParamsChanged(Sender: TObject);
begin
  UpdateTileLayer;
end;

function TTMSFNCOpenLayersTileLayer.GetID: string;
begin
  if (FID = '') or FRecreate then
    FID := CreateNewGUID;

  FRecreate := False;
  Result := FID;
end;

function TTMSFNCOpenLayersTileLayer.IsOpacityStored: Boolean;
begin
  Result := Opacity <> 0;
end;

function TTMSFNCOpenLayersTileLayer.IsURLStored: Boolean;
begin
  Result := URL <> '';
end;

procedure TTMSFNCOpenLayersTileLayer.SetSource(
  const Value: TTMSFNCOpenLayersTileLayerSource);
begin
  if FSource <> Value then
  begin
    FSource := Value;
    UpdateTileLayer;
  end;
end;

//procedure TTMSFNCOpenLayersTileLayer.SetType(
//  const Value: TTMSFNCOpenLayersTileLayerType);
//begin
//  if FType <> Value then
//  begin
//    FType := Value;
//    UpdateTileLayer;
//  end;
//end;

procedure TTMSFNCOpenLayersTileLayer.SetCSSClassName(const Value: string);
begin
  if FCSSClassName <> Value then
  begin
    FCSSClassName := Value;
    UpdateTileLayer;
  end;
end;

procedure TTMSFNCOpenLayersTileLayer.SetOpacity(const Value: Single);
begin
  if FOpacity <> Value then
  begin
    FOpacity := Value;
    UpdateTileLayer;
  end;
end;

procedure TTMSFNCOpenLayersTileLayer.SetParams(
  const Value: TTMSFNCOpenLayersTileLayerParams);
begin
  FParams.Assign(Value);
end;

procedure TTMSFNCOpenLayersTileLayer.SetURL(const Value: string);
begin
  if FURL <> Value then
  begin
    FURL := Value;
    UpdateTileLayer;
  end;
end;

procedure TTMSFNCOpenLayersTileLayer.UpdateTileLayer;
begin
  FReload := True;
  if Assigned(FOwner) then
    (FOwner as TTMSFNCCustomOpenLayers).UpdateTileLayers;
end;

{ TTMSFNCOpenLayersTileLayers }

function TTMSFNCOpenLayersTileLayers.Add: TTMSFNCOpenLayersTileLayer;
begin
  Result := TTMSFNCOpenLayersTileLayer(inherited Add);
end;

procedure TTMSFNCOpenLayersTileLayers.Clear;
var
  l: TTMSFNCCustomMaps;
  ci: TCollectionItem;
begin
  l := FOwner;
  if Assigned(l) then
    l.BeginUpdate;

  if Count > 0 then
  begin
    while Count > 0 do
    begin
      ci := TCollectionItem(Items[Count - 1]);
      ci.Free;
    end;
  end;

  if Assigned(l) then
    l.EndUpdate;
end;

constructor TTMSFNCOpenLayersTileLayers.Create(AOwner: TTMSFNCCustomMaps);
begin
  inherited Create(AOwner, CreateItemClass);
  FOwner := AOwner;
end;

function TTMSFNCOpenLayersTileLayers.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersTileLayer;
end;

function TTMSFNCOpenLayersTileLayers.GetItem(
  Index: Integer): TTMSFNCOpenLayersTileLayer;
begin
  Result := TTMSFNCOpenLayersTileLayer(inherited Items[Index]);
end;

function TTMSFNCOpenLayersTileLayers.GetOwner: TPersistent;
begin
  Result := FOwner;
end;

function TTMSFNCOpenLayersTileLayers.Insert(
  Index: Integer): TTMSFNCOpenLayersTileLayer;
begin
  Result := TTMSFNCOpenLayersTileLayer(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersTileLayers.Recreate;
var
  I: Integer;
begin
  for I := 0 to Count - 1 do
    Items[I].Recreate := True;
end;

procedure TTMSFNCOpenLayersTileLayers.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersTileLayer);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCOpenLayers }

procedure TTMSFNCOpenLayers.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClass(TTMSFNCOpenLayers);
end;

procedure TTMSFNCOpenLayers.AddBridge(ABridgeName: string; ABridgeObject: TObject);
begin
  inherited AddBridge(ABridgeName, ABridgeObject);
end;

function TTMSFNCOpenLayers.CanGoBack: Boolean;
begin
  Result := inherited CanGoBack;
end;

function TTMSFNCOpenLayers.CanGoForward: Boolean;
begin
  Result := inherited CanGoForward;
end;

procedure TTMSFNCOpenLayers.CaptureScreenShot(ACompleteEvent: TTMSFNCWebBrowserCaptureScreenShotCompleteEvent = nil);
begin
  inherited CaptureScreenShot(ACompleteEvent);
end;

procedure TTMSFNCOpenLayers.ClearCache;
begin
  inherited ClearCache;
end;

procedure TTMSFNCOpenLayers.DeInitialize;
begin
  inherited DeInitialize;
end;

procedure TTMSFNCOpenLayers.ExecuteJavaScript(AScript: String;
  ACompleteEvent: TTMSFNCWebBrowserJavaScriptCompleteEvent = nil; AImmediate: Boolean = False);
begin
  inherited ExecuteJavaScript(AScript, ACompleteEvent, AImmediate);
end;

function TTMSFNCOpenLayers.ExecuteJavaScriptSync(AScript: String): string;
begin
  Result := inherited ExecuteJavaScriptSync(AScript);
end;

function TTMSFNCOpenLayers.GetBridgeCommunicationLayer(ABridgeName: string): string;
begin
  Result := inherited GetBridgeCommunicationLayer(ABridgeName);
end;

procedure TTMSFNCOpenLayers.GetHeadLinks(AList: TTMSFNCMapsLinksList;
  ACheckReady: Boolean);
{$IFNDEF WEBLIB}
var
  I: Integer;
  l: TTMSFNCMapsLink;
{$ENDIF}
begin
  inherited;

  {$IFNDEF WEBLIB}
  if not IsDesigning and (LibraryLocation = llOffline) then
  begin
    for I := AList.Count - 1 downto 0 do
    begin
      l := AList[I];
      if (l.URL = OPENLAYERSAPIURL + GetAPIVersion + '/ol.css') or (l.URL = OPENLAYERSAPIURL + GetAPIVersion + '/dist/ol.js') then
        AList.Delete(I);
    end;

    AList.Add(TTMSFNCMapsLink.CreateLink(TTMSFNCUtils.AddBackslash(TTMSFNCUtils.GetTempPath) + 'ttmsfncopenlayers/ol.css', 'text/css', 'stylesheet'));
    AList.Add(TTMSFNCMapsLink.CreateScript(TTMSFNCUtils.AddBackslash(TTMSFNCUtils.GetTempPath) + 'ttmsfncopenlayers/ol.js', 'text/javascript'));
  end;
  {$ENDIF}
end;

{$IFDEF ANDROID}
function TTMSFNCOpenLayers.NativeDialog: Pointer;
begin
  Result := inherited NativeDialog;
end;
{$ENDIF}

{$IFDEF MSWINDOWS}
function TTMSFNCOpenLayers.GetWebBrowserInstance: IInterface;
begin
  Result := inherited GetWebBrowserInstance;
end;
{$ENDIF}

procedure TTMSFNCOpenLayers.GoBack;
begin
  inherited GoBack;
end;

procedure TTMSFNCOpenLayers.GoForward;
begin
  inherited GoForward;
end;

procedure TTMSFNCOpenLayers.Initialize;
begin
  inherited Initialize;
end;

function TTMSFNCOpenLayers.IsFMXBrowser: Boolean;
begin
  Result := inherited IsFMXBrowser;
end;

procedure TTMSFNCOpenLayers.LoadFile(AFile: String);
begin
  inherited LoadFile(AFile);
end;

procedure TTMSFNCOpenLayers.LoadHTML(AHTML: String);
begin
  inherited LoadHTML(AHTML);
end;

function TTMSFNCOpenLayers.NativeBrowser: Pointer;
begin
  Result := inherited NativeBrowser;
end;

function TTMSFNCOpenLayers.NativeEnvironment: Pointer;
begin
  Result := inherited NativeEnvironment;
end;

procedure TTMSFNCOpenLayers.Navigate;
begin
  inherited Navigate;
end;

procedure TTMSFNCOpenLayers.Navigate(const AURL: string);
begin
  inherited Navigate(AURL);
end;

procedure TTMSFNCOpenLayers.Reload;
begin
  inherited Reload;
end;

procedure TTMSFNCOpenLayers.RemoveBridge(ABridgeName: string);
begin
  inherited RemoveBridge(ABridgeName);
end;

procedure TTMSFNCOpenLayers.ShowDebugConsole;
begin
  inherited ShowDebugConsole;
end;

procedure TTMSFNCOpenLayers.StopLoading;
begin
  inherited StopLoading;
end;

{ TTMSFNCOpenLayersPolyline }

procedure TTMSFNCOpenLayersPolyline.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCOpenLayersPolyline then
  begin
    FLabel.Assign((Source as TTMSFNCOpenLayersPolyline).FLabel);
    FDraggable := (Source as TTMSFNCOpenLayersPolyline).Draggable;
  end;
end;

constructor TTMSFNCOpenLayersPolyline.Create(ACollection: TCollection);
begin
  inherited;
  FLabel := TTMSFNCOpenLayersLabel.Create;
  FLabel.OnChange := @LabelChanged;
  FDraggable := False;
end;

destructor TTMSFNCOpenLayersPolyline.Destroy;
begin
  FLabel.Free;
  inherited;
end;

procedure TTMSFNCOpenLayersPolyline.LabelChanged(Sender: TObject);
begin
  UpdatePolyElement;
end;

procedure TTMSFNCOpenLayersPolyline.SetDraggable(const Value: Boolean);
begin
  FDraggable := Value;
  UpdatePolyElement;
end;

procedure TTMSFNCOpenLayersPolyline.SetLabel(
  const Value: TTMSFNCOpenLayersLabel);
begin
  FLabel.Assign(Value);
  UpdatePolyElement;
end;

{ TTMSFNCOpenLayersPolylines }

function TTMSFNCOpenLayersPolylines.Add: TTMSFNCOpenLayersPolyline;
begin
  Result := TTMSFNCOpenLayersPolyline(inherited Add);
end;

function TTMSFNCOpenLayersPolylines.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersPolyline;
end;

function TTMSFNCOpenLayersPolylines.GetItem(
  Index: Integer): TTMSFNCOpenLayersPolyline;
begin
  Result := TTMSFNCOpenLayersPolyline(inherited Items[Index]);
end;

function TTMSFNCOpenLayersPolylines.Insert(
  Index: Integer): TTMSFNCOpenLayersPolyline;
begin
  Result := TTMSFNCOpenLayersPolyline(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersPolylines.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersPolyline);
begin
  inherited Items[Index] := Value;
end;


{ TTMSFNCOpenLayersPolygon }

function TTMSFNCOpenLayersPolygon.AddHole(
  ACoordinates: TTMSFNCMapsCoordinateRecArray): TTMSFNCMapsPolyElementHole;
begin
  Result := inherited AddHole(ACoordinates);
end;

procedure TTMSFNCOpenLayersPolygon.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCOpenLayersPolygon then
  begin
    FLabel.Assign((Source as TTMSFNCOpenLayersPolygon).FLabel);
    FDraggable := (Source as TTMSFNCOpenLayersPolygon).Draggable;
  end;
end;

constructor TTMSFNCOpenLayersPolygon.Create(ACollection: TCollection);
begin
  inherited;
  FLabel := TTMSFNCOpenLayersLabel.Create;
  FLabel.OnChange := @LabelChanged;
  FDraggable := False;
end;

destructor TTMSFNCOpenLayersPolygon.Destroy;
begin
  FLabel.Free;
  inherited;
end;

procedure TTMSFNCOpenLayersPolygon.LabelChanged(Sender: TObject);
begin
  UpdatePolyElement;
end;

procedure TTMSFNCOpenLayersPolygon.SetDraggable(const Value: Boolean);
begin
  FDraggable := Value;
  UpdatePolyElement;
end;

procedure TTMSFNCOpenLayersPolygon.SetLabel(
  const Value: TTMSFNCOpenLayersLabel);
begin
  FLabel.Assign(Value);
  UpdatePolyElement;
end;

{ TTMSFNCOpenLayersPolygons }

function TTMSFNCOpenLayersPolygons.Add: TTMSFNCOpenLayersPolygon;
begin
  Result := TTMSFNCOpenLayersPolygon(inherited Add);
end;

function TTMSFNCOpenLayersPolygons.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersPolygon;
end;

function TTMSFNCOpenLayersPolygons.GetItem(
  Index: Integer): TTMSFNCOpenLayersPolygon;
begin
  Result := TTMSFNCOpenLayersPolygon(inherited Items[Index]);
end;

function TTMSFNCOpenLayersPolygons.Insert(
  Index: Integer): TTMSFNCOpenLayersPolygon;
begin
  Result := TTMSFNCOpenLayersPolygon(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersPolygons.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersPolygon);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCOpenLayersLabel }

procedure TTMSFNCOpenLayersLabel.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCOpenLayersLabel) then
  begin
    FText := (Source as TTMSFNCOpenLayersLabel).Text;
    FOffsetX := (Source as TTMSFNCOpenLayersLabel).OffsetX;
    FOffsetY := (Source as TTMSFNCOpenLayersLabel).OffsetY;
    FFontColor := (Source as TTMSFNCOpenLayersLabel).FontColor;
    FFontSize := (Source as TTMSFNCOpenLayersLabel).FontSize;
    FBackgroundColor := (Source as TTMSFNCOpenLayersLabel).BackgroundColor;
    FBorderColor := (Source as TTMSFNCOpenLayersLabel).BorderColor;
    FPadding := (Source as TTMSFNCOpenLayersLabel).Padding;
  end
  else
    inherited;
end;

procedure TTMSFNCOpenLayersLabel.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTMSFNCOpenLayersLabel.Create;
begin
  FText := '';
  FOffsetX := 0;
  FOffsetY := 0;
  FFontColor := gcBlack;
  FFontSize := 12;
  FBackgroundColor := gcWhite;
  FBorderColor := gcBlack;
  FPadding := 3;
end;

procedure TTMSFNCOpenLayersLabel.SetBackgroundColor(
  const Value: TTMSFNCGraphicsColor);
begin
  if FBackgroundColor <> Value then
  begin
    FBackgroundColor := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetBorderColor(
  const Value: TTMSFNCGraphicsColor);
begin
  if FBorderColor <> Value then
  begin
    FBorderColor := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetFontColor(
  const Value: TTMSFNCGraphicsColor);
begin
  if FFontColor <> Value then
  begin
    FFontColor := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetFontSize(const Value: Integer);
begin
  if FFontSize <> Value then
  begin
    FFontSize := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetOffsetX(const Value: Double);
begin
  if FOffsetX <> Value then
  begin
    FOffsetX := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetOffsetY(const Value: Double);
begin
  if FOffsetY <> Value then
  begin
    FOffsetY := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetPadding(const Value: Integer);
begin
  if FPadding <> Value then
  begin
    FPadding := Value;
    Changed;
  end;
end;

procedure TTMSFNCOpenLayersLabel.SetText(const Value: string);
begin
  if FText <> Value then
  begin
    FText := Value;
    Changed;
  end;
end;

{ TTMSFNCOpenLayersMarker }

procedure TTMSFNCOpenLayersMarker.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCOpenLayersMarker then
  begin
    FDefaultAnchor := (Source as TTMSFNCOpenLayersMarker).DefaultAnchor;
    FAnchor.Assign((Source as TTMSFNCOpenLayersMarker).Anchor);
    FDraggable := (Source as TTMSFNCOpenLayersMarker).Draggable;
  end;
end;

constructor TTMSFNCOpenLayersMarker.Create(ACollection: TCollection);
begin
  inherited;
  FDraggable := False;
  FDefaultAnchor := True;
  FAnchor := TTMSFNCMapsAnchorPoint.Create;
  FAnchor.OnChange := {$IFDEF LCLWEBLIB}@{$ENDIF}DoAnchorChanged;
end;

destructor TTMSFNCOpenLayersMarker.Destroy;
begin
  FAnchor.Free;
  inherited;
end;

procedure TTMSFNCOpenLayersMarker.DoAnchorChanged(Sender: TObject);
begin
  UpdateMarker;
end;

procedure TTMSFNCOpenLayersMarker.SetAnchor(const Value: TTMSFNCMapsAnchorPoint);
begin
  if FAnchor <> Value then
    FAnchor.Assign(Value);
end;

procedure TTMSFNCOpenLayersMarker.SetDefaultAnchor(const Value: Boolean);
begin
  if FDefaultAnchor <> Value then
  begin
    FDefaultAnchor := Value;
    UpdateMarker;
  end;
end;

procedure TTMSFNCOpenLayersMarker.SetDraggable(const Value: Boolean);
begin
  if (Draggable <> Value) then
  begin
    FDraggable := Value;
    UpdateMarker;
  end;
end;

{ TTMSFNCOpenLayersMarkers }

function TTMSFNCOpenLayersMarkers.Add: TTMSFNCOpenLayersMarker;
begin
  Result := TTMSFNCOpenLayersMarker(inherited Add);
end;

function TTMSFNCOpenLayersMarkers.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersMarker;
end;

function TTMSFNCOpenLayersMarkers.GetItem(Index: Integer): TTMSFNCOpenLayersMarker;
begin
  Result := TTMSFNCOpenLayersMarker(inherited Items[Index]);
end;

function TTMSFNCOpenLayersMarkers.Insert(Index: Integer): TTMSFNCOpenLayersMarker;
begin
  Result := TTMSFNCOpenLayersMarker(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersMarkers.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersMarker);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCOpenLayersCircle }

procedure TTMSFNCOpenLayersCircle.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCOpenLayersCircle then
  begin
    FDraggable := (Source as TTMSFNCOpenLayersCircle).Draggable;
  end;
end;

constructor TTMSFNCOpenLayersCircle.Create(ACollection: TCollection);
begin
  inherited;
  FDraggable := False;
end;

destructor TTMSFNCOpenLayersCircle.Destroy;
begin
  inherited;
end;

procedure TTMSFNCOpenLayersCircle.SetDraggable(const Value: Boolean);
begin
  if (FDraggable <> Value) then
  begin
    FDraggable := Value;
    UpdatePolyElement;
  end;
end;

{ TTMSFNCOpenLayersCircles }

function TTMSFNCOpenLayersCircles.Add: TTMSFNCOpenLayersCircle;
begin
  Result := TTMSFNCOpenLayersCircle(inherited Add);
end;

function TTMSFNCOpenLayersCircles.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersCircle;
end;

function TTMSFNCOpenLayersCircles.GetItem(
  Index: Integer): TTMSFNCOpenLayersCircle;
begin
  Result := TTMSFNCOpenLayersCircle(inherited Items[Index]);
end;

function TTMSFNCOpenLayersCircles.Insert(
  Index: Integer): TTMSFNCOpenLayersCircle;
begin
  Result := TTMSFNCOpenLayersCircle(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersCircles.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersCircle);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCOpenLayersRectangle }

procedure TTMSFNCOpenLayersRectangle.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCOpenLayersRectangle then
  begin
    FDraggable := (Source as TTMSFNCOpenLayersRectangle).Draggable;
  end;
end;

constructor TTMSFNCOpenLayersRectangle.Create(ACollection: TCollection);
begin
  inherited;
  FDraggable := False;
end;

destructor TTMSFNCOpenLayersRectangle.Destroy;
begin
  inherited;
end;

procedure TTMSFNCOpenLayersRectangle.SetDraggable(const Value: Boolean);
begin
  if (FDraggable <> Value) then
  begin
    FDraggable := Value;
    UpdatePolyElement;
  end;
end;

{ TTMSFNCOpenLayersRectangles }

function TTMSFNCOpenLayersRectangles.Add: TTMSFNCOpenLayersRectangle;
begin
  Result := TTMSFNCOpenLayersRectangle(inherited Add);
end;

function TTMSFNCOpenLayersRectangles.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersRectangle;
end;

function TTMSFNCOpenLayersRectangles.GetItem(
  Index: Integer): TTMSFNCOpenLayersRectangle;
begin
  Result := TTMSFNCOpenLayersRectangle(inherited Items[Index]);
end;

function TTMSFNCOpenLayersRectangles.Insert(
  Index: Integer): TTMSFNCOpenLayersRectangle;
begin
  Result := TTMSFNCOpenLayersRectangle(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersRectangles.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersRectangle);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCOpenLayersTileLayerParams }

procedure TTMSFNCOpenLayersTileLayerParams.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCOpenLayersTileLayerParams then
  begin
    FLayers := (Source as TTMSFNCOpenLayersTileLayerParams).Layers;
  end;
end;

procedure TTMSFNCOpenLayersTileLayerParams.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTMSFNCOpenLayersTileLayerParams.Create;
begin
end;

procedure TTMSFNCOpenLayersTileLayerParams.SetLayers(const Value: string);
begin
  if FLayers <> Value then
  begin
    FLayers := Value;
    Changed;
  end;
end;

{ TTMSFNCOpenLayersClusters }

function TTMSFNCOpenLayersClusters.Add: TTMSFNCOpenLayersCluster;
begin
  Result := TTMSFNCOpenLayersCluster(inherited Add);
end;

procedure TTMSFNCOpenLayersClusters.Clear;
var
  l: TTMSFNCCustomMaps;
  ci: TCollectionItem;
begin
  l := FOwner;
  if Assigned(l) then
    l.BeginUpdate;

  if Count > 0 then
  begin
    while Count > 0 do
    begin
      ci := TCollectionItem(Items[Count - 1]);
      ci.Free;
    end;
  end;

  if Assigned(l) then
    l.EndUpdate;
end;

constructor TTMSFNCOpenLayersClusters.Create(AOwner: TTMSFNCCustomMaps);
begin
  inherited Create(AOwner, CreateItemClass);
  FOwner := AOwner;
end;

function TTMSFNCOpenLayersClusters.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersCluster;
end;

function TTMSFNCOpenLayersClusters.GetItem(
  Index: Integer): TTMSFNCOpenLayersCluster;
begin
  Result := TTMSFNCOpenLayersCluster(inherited Items[Index]);
end;

function TTMSFNCOpenLayersClusters.GetOwner: TPersistent;
begin
  Result := FOwner;
end;

function TTMSFNCOpenLayersClusters.Insert(
  Index: Integer): TTMSFNCOpenLayersCluster;
begin
  Result := TTMSFNCOpenLayersCluster(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersClusters.Recreate;
var
  I: Integer;
begin
  for I := 0 to Count - 1 do
    Items[I].Recreate := True;
end;

procedure TTMSFNCOpenLayersClusters.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersCluster);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCOpenLayersCluster }

procedure TTMSFNCOpenLayersCluster.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCOpenLayersCluster then
  begin
    FImagePath := (Source as TTMSFNCOpenLayersCluster).ImagePath;
    FFontColor := (Source as TTMSFNCOpenLayersCluster).FontColor;
    FFillColor := (Source as TTMSFNCOpenLayersCluster).FillColor;
    FStrokeColor := (Source as TTMSFNCOpenLayersCluster).StrokeColor;
  end;
end;

constructor TTMSFNCOpenLayersCluster.Create(ACollection: TCollection);
begin
  inherited;
  FImagePath := '';
  FFontColor := gcWhite;
  FillColor := gcLightskyblue;
  StrokeColor := gcWhite;

  if Assigned(ACollection) then
    FOwner := (Collection as TTMSFNCOpenLayersClusters).FOwner;

  UpdateCluster;
end;

destructor TTMSFNCOpenLayersCluster.Destroy;
begin
  inherited;
  if Assigned(FMarkers) then
    FMarkers.Free;
  UpdateCluster;
end;

function TTMSFNCOpenLayersCluster.GetID: string;
begin
  if (FID = '') or FRecreate then
    FID := CreateNewGUID;

  FRecreate := False;
  Result := FID;
end;

function TTMSFNCOpenLayersCluster.GetMarkers: TTMSFNCOpenLayersMarkersList;
var
  I: Integer;
  m: TTMSFNCOpenLayersMarker;
begin
  if not Assigned(FMarkers) then
    FMarkers := TTMSFNCOpenLayersMarkersList.Create;

  FMarkers.Clear;

  if Assigned(FOwner) then
  begin
    for I := 0 to TTMSFNCCustomOpenLayers(FOwner).Markers.Count - 1 do
    begin
      m := TTMSFNCCustomOpenLayers(FOwner).Markers[I];
      if m.Cluster = Self then
        FMarkers.Add(m);
    end;
  end;

  Result := FMarkers;
end;

function TTMSFNCOpenLayersCluster.IsImagePathStored: Boolean;
begin
  Result := ImagePath <> '';
end;

procedure TTMSFNCOpenLayersCluster.SetFillColor(
  const Value: TTMSFNCGraphicsColor);
begin
  if FFillColor <> Value then
  begin
    FFillColor := Value;
    UpdateCluster;
  end;
end;

procedure TTMSFNCOpenLayersCluster.SetFontColor(
  const Value: TTMSFNCGraphicsColor);
begin
  if FFontColor <> Value then
  begin
    FFontColor := Value;
    UpdateCluster;
  end;
end;

procedure TTMSFNCOpenLayersCluster.SetImagePath(const Value: string);
begin
  if FImagePath <> Value then
  begin
    FImagePath := Value;
    UpdateCluster;
  end;
end;

procedure TTMSFNCOpenLayersCluster.SetStrokeColor(
  const Value: TTMSFNCGraphicsColor);
begin
  if FStrokeColor <> Value then
  begin
    FStrokeColor := Value;
    UpdateCluster;
  end;
end;

procedure TTMSFNCOpenLayersCluster.UpdateCluster;
begin
  FReload := True;
  if Assigned(FOwner) then
    (FOwner as TTMSFNCCustomOpenLayers).UpdateClusters;
end;

{$IFDEF WEBLIB}
function TTMSFNCOpenLayersMarkersList.GetItem(Index: Integer): TTMSFNCOpenLayersMarker;
begin
  Result := TTMSFNCOpenLayersMarker(inherited Items[Index]);
end;

procedure TTMSFNCOpenLayersMarkersList.SetItem(Index: Integer; const Value: TTMSFNCOpenLayersMarker);
begin
  inherited Items[Index] := Value;
end;
{$ENDIF}


{ TTMSFNCOpenLayersHeatMap }

procedure TTMSFNCOpenLayersHeatMap.Assign(Source: TPersistent);
begin
  inherited;
end;

constructor TTMSFNCOpenLayersHeatMap.Create(ACollection: TCollection);
begin
  inherited;
end;

destructor TTMSFNCOpenLayersHeatMap.Destroy;
begin
  inherited;
end;

{ TTMSFNCOpenLayersHeatMaps }

function TTMSFNCOpenLayersHeatMaps.Add: TTMSFNCOpenLayersHeatMap;
begin
  Result := TTMSFNCOpenLayersHeatMap(inherited Add);
end;

function TTMSFNCOpenLayersHeatMaps.CreateItemClass: TCollectionItemClass;
begin
  Result := TTMSFNCOpenLayersHeatMap;
end;

function TTMSFNCOpenLayersHeatMaps.GetItem(
  Index: Integer): TTMSFNCOpenLayersHeatMap;
begin
  Result := TTMSFNCOpenLayersHeatMap(inherited Items[Index]);
end;

function TTMSFNCOpenLayersHeatMaps.Insert(
  Index: Integer): TTMSFNCOpenLayersHeatMap;
begin
  Result := TTMSFNCOpenLayersHeatMap(inherited Insert(Index));
end;

procedure TTMSFNCOpenLayersHeatMaps.SetItem(Index: Integer;
  const Value: TTMSFNCOpenLayersHeatMap);
begin
  inherited Items[Index] := Value;
end;


end.
